import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    const initialize = async () => {
      const storedUser = authService.getStoredUser();

      if (storedUser && authService.isAuthenticated()) {
        setUser(storedUser);
        try {
          const freshUser = await authService.getProfile();
          if (!ignore && freshUser) {
            setUser(freshUser);
          }
        } catch (error) {
          authService.logout();
          if (!ignore) {
            setUser(null);
          }
        } finally {
          if (!ignore) {
            setLoading(false);
          }
        }
        return;
      }

      if (!ignore) {
        setLoading(false);
      }
    };

    void initialize();

    return () => {
      ignore = true;
    };
  }, []);

  const login = async (phone, password) => {
    const data = await authService.login(phone, password);
    try {
      const profile = await authService.getProfile({ forceRefresh: true });
      setUser(profile ?? data.user ?? null);
    } catch (error) {
      setUser(data.user ?? null);
    }
    return data;
  };

  const register = async (phone, password, name) => {
    const data = await authService.register(phone, password, name);
    try {
      const profile = await authService.getProfile({ forceRefresh: true });
      setUser(profile ?? data.user ?? null);
    } catch (error) {
      setUser(data.user ?? null);
    }
    return data;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const updatedUser = await authService.updateProfile(profileData);
    setUser(updatedUser);
    authService.profileCache = updatedUser;
    return updatedUser;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
